import React from 'react';
import {
    Box,
    IconButton,
    TablePagination,
    Pagination,
    Typography,
    type SxProps,
    type Theme,
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { theme } from '../../constants/theme';
import { useThemeContext } from '../../hook/useThemeManager';
import { IPagination } from '../../hook/usePagination';
import {
    brandContestPaginationCompactShellSx,
    brandContestPaginationTableSx,
    editorialSelectMenuProps,
} from '../../pages/BrandDashboard/contestWizard/midnightStyles';

interface ICustomPagination {
    pagination: IPagination;
    onRowsChange: (params: any) => void;
    onPageChange: (params: any) => void;
    rowsPerPageOptions: number[];
    /** Outer wrapper styles (page gutters, bottom inset for floating widgets, etc.) */
    sx?: SxProps<Theme>;
    /** Brand contest drawer: single centered row for arrows + rows-per-page + range. */
    variant?: 'default' | 'brand';
}

const CustomPagination: React.FC<ICustomPagination> = ({
    pagination,
    onPageChange,
    onRowsChange,
    rowsPerPageOptions,
    sx,
    variant = 'default',
}) => {
    const { themeStyle } = useThemeContext();
    const isBrand = variant === 'brand';
    const CustomPaginationActions = () => (isBrand ? null : <span />);

    const handlePageChange = (_: unknown, page: number) => {
        onPageChange(page);
    };

    const handleRowsChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        onRowsChange(+event.target.value);
        onPageChange(1);
    };

    const pageCount = Math.max(1, Math.ceil(pagination.totalCount / pagination.rows) || 1);
    const currentPage = Math.min(Math.max(1, pagination.page), pageCount);

    const brandPageButtonSx: SxProps<Theme> = {
        width: { xs: 30, sm: 36 },
        height: { xs: 30, sm: 36 },
        borderRadius: 1,
        border: '1px solid rgba(72,72,71,0.45)',
        bgcolor: 'rgba(30,30,30,1)',
        color: 'rgba(173,170,170,0.95)',
        '&:hover': {
            color: theme[themeStyle].primary_orange,
            bgcolor: 'rgba(37,37,37,1)',
        },
        '&.Mui-disabled': {
            opacity: 0.35,
            color: 'rgba(173,170,170,0.5)',
            borderColor: 'rgba(72,72,71,0.25)',
        },
    };

    const brandCurrentPageSx: SxProps<Theme> = {
        minWidth: { xs: 30, sm: 36 },
        height: { xs: 30, sm: 36 },
        px: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: 1,
        border: `2px solid ${theme[themeStyle].primary_orange}`,
        boxShadow: `0 0 15px ${theme[themeStyle].primary_orange}33`,
        bgcolor: 'rgba(30,30,30,1)',
        color: theme[themeStyle].primary_orange,
        fontWeight: 700,
        fontSize: { xs: '0.68rem', sm: '0.78rem' },
        fontVariantNumeric: 'tabular-nums',
    };

    return (
        <Box
            sx={{
                width: '100%',
                boxSizing: 'border-box',
                ...sx,
            }}>
            <Box
                sx={{
                    bgcolor: 'rgba(14,14,14,0.8)',
                    backdropFilter: 'blur(16px)',
                    border: '1px solid rgba(72,72,71,0.35)',
                    borderRadius: 2,
                    px: { xs: 1, sm: 2, md: 3 },
                    py: { xs: 1.25, sm: 2, md: 2.5 },
                    display: 'flex',
                    flexDirection: isBrand ? 'row' : { xs: 'column', sm: 'row' },
                    alignItems: 'center',
                    justifyContent: isBrand ? 'center' : { xs: 'stretch', sm: 'space-between' },
                    gap: isBrand ? 2.5 : { xs: 1.25, sm: 3 },
                    flexWrap: isBrand ? 'nowrap' : { xs: 'nowrap', sm: 'wrap' },
                    maxWidth: '100%',
                    minWidth: 0,
                    overflow: 'hidden',
                    boxSizing: 'border-box',
                    ...(isBrand ? brandContestPaginationCompactShellSx : {}),
                }}>
                {isBrand ? (
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: { xs: 0.35, sm: 0.75 },
                            flexShrink: 0,
                            '@media (max-width: 1024px)': {
                                width: '100%',
                                justifyContent: 'center',
                            },
                        }}>
                        <IconButton
                            aria-label="Previous page"
                            size="small"
                            disabled={currentPage <= 1}
                            onClick={() => onPageChange(currentPage - 1)}
                            sx={brandPageButtonSx}>
                            <ChevronLeftIcon fontSize="small" />
                        </IconButton>
                        <Typography component="span" sx={brandCurrentPageSx}>
                            {currentPage}
                        </Typography>
                        <IconButton
                            aria-label="Next page"
                            size="small"
                            disabled={currentPage >= pageCount}
                            onClick={() => onPageChange(currentPage + 1)}
                            sx={brandPageButtonSx}>
                            <ChevronRightIcon fontSize="small" />
                        </IconButton>
                    </Box>
                ) : (
                    <Pagination
                        count={pageCount}
                        variant="outlined"
                        page={currentPage}
                        shape="rounded"
                        siblingCount={1}
                        boundaryCount={1}
                        showFirstButton
                        showLastButton
                        onChange={(_: unknown, page: number) => {
                            handlePageChange(_, page);
                        }}
                        sx={{
                            flexShrink: 0,
                            alignSelf: 'center',
                            maxWidth: '100%',
                            overflowX: 'auto',
                            '& .MuiPagination-ul': {
                                gap: { xs: 0.35, sm: 0.75 },
                                flexWrap: 'nowrap',
                            },
                            '& .MuiPaginationItem-root': {
                                width: { xs: 30, sm: 36 },
                                height: { xs: 30, sm: 36 },
                                minWidth: { xs: 30, sm: 36 },
                                borderRadius: 1,
                                border: '1px solid rgba(72,72,71,0.45)',
                                bgcolor: 'rgba(30,30,30,1)',
                                color: 'rgba(173,170,170,0.95)',
                                fontWeight: 700,
                                fontSize: { xs: '0.68rem', sm: '0.78rem' },
                                '&:hover': {
                                    color: theme[themeStyle].primary_orange,
                                    bgcolor: 'rgba(37,37,37,1)',
                                },
                            },
                            '& .Mui-selected': {
                                color: theme[themeStyle].primary_orange,
                                border: `2px solid ${theme[themeStyle].primary_orange}`,
                                boxShadow: `0 0 15px ${theme[themeStyle].primary_orange}33`,
                                bgcolor: 'rgba(30,30,30,1)',
                            },
                        }}
                    />
                )}

                <Box
                    display="flex"
                    alignItems="center"
                    justifyContent={isBrand ? 'center' : 'flex-end'}
                    sx={{
                        flex: isBrand ? '0 0 auto' : '1 1 auto',
                        minWidth: isBrand ? 0 : { xs: 0, sm: 220, md: 260 },
                        width: isBrand ? 'auto' : { xs: '100%', sm: 'auto' },
                        overflow: 'hidden',
                        ...(isBrand
                            ? {
                                  '@media (max-width: 1024px)': {
                                      width: '100%',
                                      maxWidth: '100%',
                                      justifyContent: 'center',
                                      overflow: 'visible',
                                  },
                              }
                            : {}),
                    }}>
                    <TablePagination
                        component="div"
                        count={pagination.totalCount}
                        page={pagination.page - 1}
                        onPageChange={handlePageChange}
                        rowsPerPage={pagination.rows}
                        onRowsPerPageChange={handleRowsChange}
                        rowsPerPageOptions={rowsPerPageOptions}
                        ActionsComponent={CustomPaginationActions}
                        SelectProps={
                            isBrand
                                ? {
                                      MenuProps: editorialSelectMenuProps,
                                  }
                                : undefined
                        }
                        sx={{
                            color: 'rgba(173,170,170,0.95)',
                            minHeight: 0,
                            width: isBrand ? 'auto' : '100%',
                            maxWidth: '100%',
                            '& .MuiToolbar-root': {
                                minHeight: isBrand ? 40 : 0,
                                px: 0,
                                py: 0,
                                gap: isBrand ? 1.5 : { xs: 0.5, sm: 1.5 },
                                justifyContent: isBrand ? 'center' : { xs: 'space-between', sm: 'flex-end' },
                                alignItems: 'center',
                                flexWrap: isBrand ? 'nowrap' : 'wrap',
                            },
                            ...(isBrand
                                ? {
                                      '& .MuiTablePagination-spacer': { display: 'none' },
                                      '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                                          display: 'flex',
                                          alignItems: 'center',
                                      },
                                      '& .MuiTablePagination-input': {
                                          display: 'flex',
                                          alignItems: 'center',
                                      },
                                      ...brandContestPaginationTableSx,
                                  }
                                : {}),
                            '& .MuiTablePagination-selectLabel': {
                                fontSize: { xs: '0.62rem', sm: '0.7rem' },
                                textTransform: 'uppercase',
                                letterSpacing: '0.12em',
                                color: 'rgba(173,170,170,0.75)',
                                fontWeight: 700,
                                m: 0,
                            },
                            '& .MuiTablePagination-input': {
                                border: '1px solid rgba(72,72,71,0.45)',
                                bgcolor: 'rgba(30,30,30,1)',
                                borderRadius: 1,
                                px: { xs: 0.75, sm: 1.25 },
                                py: { xs: 0.25, sm: 0.5 },
                                color: '#fff',
                                fontSize: { xs: '0.68rem', sm: '0.78rem' },
                                fontWeight: 700,
                            },
                            '& .MuiTablePagination-displayedRows': {
                                fontSize: { xs: '0.64rem', sm: '0.74rem' },
                                color: '#fff',
                                letterSpacing: { xs: '0.04em', sm: '0.08em' },
                                textTransform: 'uppercase',
                                whiteSpace: 'nowrap',
                                m: 0,
                            },
                            '& .MuiSvgIcon-root': {
                                color: theme[themeStyle].primary_orange,
                                fontSize: { xs: 20, sm: 24 },
                            },
                        }}
                    />
                </Box>
            </Box>
        </Box>
    );
};

export { CustomPagination };
